import { useEffect, useId, useRef, type FormEvent, type ReactNode } from "react";
import { Icon } from "./Icon";

interface DialogProps {
  open: boolean;
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
  className?: string;
}

export function Dialog({ open, title, description, children, onClose, className = "" }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    let focusFrame = 0;
    if (open) {
      if (!dialog.open) dialog.showModal();
      focusFrame = window.requestAnimationFrame(() => {
        const target = dialog.querySelector<HTMLElement>("[autofocus]")
          ?? dialog.querySelector<HTMLElement>(".dialog-form input:not([disabled]), .dialog-form textarea:not([disabled]), .dialog-form select:not([disabled])")
          ?? dialog.querySelector<HTMLElement>(".dialog-actions button:not(.button-danger)")
          ?? dialog.querySelector<HTMLElement>("button:not([disabled])");
        target?.focus();
      });
    }
    if (!open && dialog.open) dialog.close();
    return () => window.cancelAnimationFrame(focusFrame);
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={`dialog ${className}`}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
    >
      <div className="dialog-heading">
        <div>
          <h2 id={titleId}>{title}</h2>
          {description ? <p id={descriptionId}>{description}</p> : null}
        </div>
        <button className="icon-button" type="button" onClick={onClose} aria-label={`Close ${title}`}>
          <Icon name="x" />
        </button>
      </div>
      {children}
    </dialog>
  );
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  destructive = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  function submit(event: FormEvent) {
    event.preventDefault();
    onConfirm();
    onClose();
  }

  return (
    <Dialog open={open} title={title} description={description} onClose={onClose} className="dialog-small">
      <form onSubmit={submit} className="dialog-actions">
        <button type="button" className="button button-secondary" onClick={onClose}>Cancel</button>
        <button type="submit" className={destructive ? "button button-danger" : "button button-primary"}>
          {confirmLabel}
        </button>
      </form>
    </Dialog>
  );
}
